import React, { useEffect, useRef, useState } from 'react'
import { AutoField, FieldLabel, FieldProps } from '@puckeditor/core'
import { ChromePicker } from 'react-color'
import { Trash2 } from 'lucide-react'

const ChromePickerAny = ChromePicker as any
import { Popover } from '@mui/material'
import { ThemeCustomFields } from 'puck/theme-config'
import * as styles from './ColorField.module.scss'

type ColorFieldProps = FieldProps<ThemeCustomFields['colorPicker']> & {
  clearable?: boolean
  /** Allow an alpha channel — stores an `rgba(...)` string instead of hex. */
  alpha?: boolean
}

export const ColorField = ({
  field,
  value,
  onChange,
  id,
  clearable,
  alpha,
}: ColorFieldProps) => {
  const anchorEl = useRef<HTMLDivElement>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [localColor, setLocalColor] = useState(value)

  useEffect(() => {
    if (!isOpen) setLocalColor(value)
  }, [value, isOpen])

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    setLocalColor('' as any)
    onChange('' as any)
  }

  const isEmpty = !localColor

  return (
    <div>
      <div onClick={() => setIsOpen(true)}>
        <FieldLabel
          label={field.label || 'Color Picker'}
          className={styles.puckColorField}
        >
          <div
            ref={anchorEl}
            className={`${styles.puckColorFieldRow} ${clearable && !isEmpty ? styles.puckColorFieldRowClearable : ''}`}
          >
            <div
              className={`${styles.puckColorFieldPreview} ${isEmpty ? styles.puckColorFieldPreviewEmpty : ''}`}
              style={isEmpty ? undefined : { backgroundColor: localColor }}
            />

            <div className={styles.puckColorFieldInput}>
              {isEmpty ? (
                <div className={styles.puckColorFieldPlaceholder}>None</div>
              ) : (
                <div style={{ pointerEvents: 'none' }}>
                  <AutoField
                    field={{ type: 'text' }}
                    value={localColor}
                    onChange={() => {}}
                    id={id}
                  />
                </div>
              )}
            </div>

            {clearable && !isEmpty && (
              <button
                type="button"
                className={styles.puckColorFieldClear}
                onClick={handleClear}
                title="Clear color"
              >
                <Trash2 size={12} />
                Clear
              </button>
            )}
          </div>
        </FieldLabel>
      </div>

      <Popover
        open={isOpen}
        anchorEl={anchorEl.current}
        onClose={() => {
          setIsOpen(false)
          onChange(localColor)
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <ChromePickerAny
          color={localColor || '#000000'}
          onChange={(color: any) => {
            if (alpha) {
              const { r, g, b, a } = color.rgb
              // keep hex when fully opaque so values stay clean/compatible
              setLocalColor(a === 1 ? color.hex : `rgba(${r}, ${g}, ${b}, ${a})`)
            } else {
              setLocalColor(color.hex)
            }
          }}
          disableAlpha={!alpha}
        />
      </Popover>
    </div>
  )
}
